import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import loginBg from "../../assets/illustrations/login-bg.jfif";
import { T } from "../../constants/theme";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";

const passwordStrong = (p) =>
  p.length >= 10 && /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p) && /[^A-Za-z0-9]/.test(p);

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { resetPassword } = useContext(AuthContext);
  const toast = useContext(ToastContext);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!token) {
      setError("This reset link is missing its token.");
      return;
    }
    if (!passwordStrong(password)) {
      setError("Password must be at least 10 characters with upper, lower, digit and symbol");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast("Password updated — you can now sign in");
    } catch (e) {
      setError(e.message || "Unable to reset password");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (hasError) => ({
    width: "100%",
    padding: 14,
    borderRadius: 12,
    border: hasError ? "1px solid #ff4d4f" : `1px solid ${T.border}`,
    background: T.paper,
    color: T.ink,
    fontSize: 14,
    outline: "none",
    marginBottom: 10,
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "40px 20px",
        backgroundImage: `url(${loginBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.45))",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 5,
          width: "100%",
          maxWidth: 480,
          padding: 40,
          borderRadius: 24,
          background: T.surface,
          backdropFilter: "blur(20px)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.05)",
          border: `1px solid ${T.border}`,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              fontSize: 12,
              letterSpacing: 4,
              fontWeight: 700,
              color: T.muted,
              marginBottom: 10,
            }}
          >
            Account Recovery
          </div>
          <h1 style={{ margin: 0, fontSize: 30, color: T.ink }}>Set a New Password</h1>
        </div>

        {done ? (
          <>
            <div
              style={{
                background: T.goldLight,
                border: `1px solid ${T.gold}`,
                color: T.gold,
                padding: 16,
                borderRadius: 12,
                fontSize: 14,
                textAlign: "center",
                marginBottom: 20,
              }}
            >
              Your password has been updated.
            </div>
            <button
              onClick={() => navigate("/login")}
              style={{
                width: "100%",
                padding: 14,
                borderRadius: 12,
                border: "none",
                background: T.gold,
                color: T.paper,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Go to Sign In
            </button>
          </>
        ) : (
          <>
            {!token && (
              <div style={{ color: "#e53935", marginBottom: 14, fontSize: 13 }}>
                This link is missing its reset token — check the link from your email.
              </div>
            )}
            <div style={{ position: "relative" }}>
              <input
                type={showPassword ? "text" : "password"}
                style={inputStyle(!!error)}
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                style={{
                  position: "absolute",
                  right: 15,
                  top: 15,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: T.muted,
                  fontWeight: 600,
                }}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            <input
              type={showPassword ? "text" : "password"}
              style={inputStyle(!!error)}
              placeholder="Confirm New Password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />

            {error && (
              <div style={{ color: "#e53935", marginBottom: 10, fontSize: 13 }}>{error}</div>
            )}

            <button
              onClick={submit}
              disabled={loading}
              style={{
                width: "100%",
                marginTop: 8,
                padding: 14,
                borderRadius: 12,
                border: "none",
                background: T.gold,
                color: T.paper,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {loading ? "Saving..." : "Reset Password"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
